// filepath: apps/web/src/features/directory/positions/PositionsPage.tsx
//
// FR-UI-DIR-1 AC-4 — positions catalog page.
//
// Reads positions via usePositions(tenantSlug, filter). INV-1 cache
// key includes tenantSlug.
//
// ISS-516 BA-corrected wire shape (per the #516 SPA <-> Go diff  // design-adherence-ignore
// note 17855): the columns mirror the Go struct
// `internal/directory/position/package.go:22-40`.
//   - Pre-ISS-516 page used `p.code` + `p.unit_name` (phantom
//     fields the Go server never sent — silent-empty list bug
//     class per BA note_17803).
//   - Post-ISS-516 page uses `p.job_code` (the Go wire name) and
//     the canonical `status` enum (open | filled | closed) with
//     a design-system <Badge tone> surface.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { usePositions } from "../queries";
import { emptyPositionsFilter } from "../types";
import { Badge } from "@/design-system/Badge";
import type { BadgeTone } from "@/design-system/Badge";

/**
 * Map a Position status (Go enum) to a design-system Badge tone.
 * "open" = neutral (a slot is available, not yet filled)
 * "filled" = success (someone holds the position)
 * "closed" = warning (position is closed/archived, no assignment)
 */
function positionStatusTone(status: "open" | "filled" | "closed"): BadgeTone {
  switch (status) {
    case "open":
      return "neutral";
    case "filled":
      return "success";
    case "closed":
      return "warning";
  }
}

export function PositionsPage(): React.ReactElement {
  const { t } = useTranslation();
  const filter = emptyPositionsFilter();
  const query = usePositions(filter);

  switch (query.state) {
    case "loading":
      return (
        <p
          className="directory-positions__loading"
          data-testid="positions-loading"
        >
          {t("directory.positions.state.loading")}
        </p>
      );
    case "permission-denied":
      return (
        <p
          role="alert"
          className="directory-positions__permission-denied"
          data-testid="positions-permission-denied"
        >
          {t("directory.positions.state.permission_denied")}
        </p>
      );
    case "validation-error":
      return (
        <p
          role="alert"
          className="directory-positions__validation-error"
          data-testid="positions-validation-error"
        >
          {query.error?.message ??
            t("directory.positions.state.validation_error")}
        </p>
      );
    case "fetch-failure":
      return (
        <p
          role="alert"
          className="directory-positions__fetch-failure"
          data-testid="positions-fetch-failure"
        >
          {t("directory.positions.state.fetch_failure")}
        </p>
      );
    case "rate-limit":
      return (
        <p
          role="alert"
          className="directory-positions__rate-limit"
          data-testid="positions-rate-limit"
        >
          {t("directory.positions.state.rate_limit")}
        </p>
      );
    case "success": {
      const data = query.data;
      if (data === null) {
        return (
          <p
            role="alert"
            className="directory-positions__fetch-failure"
            data-testid="positions-fetch-failure"
          >
            {t("directory.positions.state.fetch_failure")}
          </p>
        );
      }
      const entries = data.items ?? [];
      return (
        <section className="directory-positions" data-testid="positions-page">
          <header className="directory-positions__header">
            <h1>{t("directory.positions.title", "Positions")}</h1>
          </header>
          {entries.length === 0 ? (
            <p
              className="directory-positions__empty"
              data-testid="positions-empty"
            >
              {t("directory.positions.empty")}
            </p>
          ) : (
            <table className="directory-positions__table">
              <thead>
                <tr>
                  <th scope="col">
                    {t("directory.positions.column.title", "Title")}
                  </th>
                  <th scope="col">
                    {t("directory.positions.column.job_code", "Job code")}
                  </th>
                  <th scope="col">
                    {t("directory.positions.column.status", "Status")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map((p) => (
                  <tr
                    key={p.id}
                    data-testid={`position-row-${p.id}`}
                    data-position-id={p.id}
                  >
                    <td>{p.title}</td>
                    <td>{p.job_code ?? ""}</td>
                    <td>
                      <Badge
                        tone={positionStatusTone(p.status)}
                        data-testid={`position-status-${p.id}`}
                      >
                        {t(`directory.positions.status.${p.status}`, p.status)}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      );
    }
  }
}
